import { DiscountCreationMode } from 'src/lib/types/discountsV2'
import { permissionType } from 'src/lib/utils/constants'

export const discountCreationModes: Record<string, DiscountCreationMode> = {
  AMOUNT_OFF: {
    label: 'Discount on cart value',
    value: 'AMOUNT_OFF',
    sidePanel: {
      title: 'Discount on cart value',
      description:
        'Applies a discount to the entire order total (either flat amount or a percentage once the customer meets the required condition (like a minimum cart value)',
      examples: [
        'Get ₹500 off on orders above ₹999.',
        'Get 20% off on orders above ₹899.',
        'Get 10% off when customers add 3 or more products to the cart.',
        'Get ₹100 off on your first order.'
      ],
      importantNote: 'The discount is applied to the subtotal before taxes and shipping.'
    },
    action: {
      type: 'LINK',
      link: '/discounts/v2/create-discount?type=AMOUNT_OFF'
    }
  },
  AMOUNT_OFF_UPTO: {
    label: 'Capped discount on cart value',
    value: 'AMOUNT_OFF_LIMIT',
    sidePanel: {
      title: 'Capped discount on cart value',
      description:
        'Applies a percentage discount on the total order value, but the discount is capped at a maximum amount. This helps merchants control discount exposure while still offering a strong incentive to shoppers.',
      examples: [
        'Get 10% off, up to a maximum of ₹300.',
        'Flat 20% off on first order, capped at ₹500.',
        'Get 10% off up to ₹350 when your cart has 3 or more items.'
      ],
      importantNote: 'The discount will never exceed the specified limit.'
    },
    action: {
      type: 'LINK',
      link: '/discounts/v2/create-discount?type=AMOUNT_OFF_UPTO'
    }
  },
  AMOUNT_OFF_PRODUCTS: {
    label: 'Discount on specific product',
    value: 'AMOUNT_OFF_PRODUCTS',
    sidePanel: {
      title: 'Discount on specific product',
      description:
        'Applies a discount only to selected products or categories, instead of the entire order. This is useful for boosting sales of targeted items or clearing inventory without affecting margins on the rest of the order.',
      examples: [
        'Flat 25% off on the Summer Collection.',
        'Get ₹150 off on all Moisturizers.',
        'Buy any 2 Serums and get ₹250 off on the second one.',
        '₹75 off on each item in the Kids wear category.'
      ],
      importantNote: 'The discount is applied only when those specific products are present in the cart.'
    },
    action: {
      type: 'LINK',
      link: '/discounts/v2/create-discount?type=AMOUNT_OFF_PRODUCTS'
    }
  },
  SHIPPING: {
    label: 'Discount on shipping',
    value: 'SHIPPING',
    sidePanel: {
      title: 'Discount on shipping',
      description:
        'Provides a discount on the shipping fee, partially or fully. Often used as a perk to improve conversion, especially for first-time buyers or for orders that meet a certain cart value.',
      examples: [
        'Free shipping on orders above ₹799.',
        'Enjoy free shipping when your cart has 3 or more items.',
        'Get 50% off on shipping when buying from the New Arrivals collection.',
        'Free shipping for new customers.'
      ],
      importantNote: 'Applies only to the shipping component of the final price.'
    },
    action: {
      type: 'LINK',
      link: '/discounts/v2/create-discount?type=SHIPPING'
    }
  },
  BUNDLE: {
    label: 'Bundle discount (Fixed price)',
    value: 'BUNDLE',
    sidePanel: {
      title: 'Bundle discount',
      description:
        'Creates a fixed-price bundle where customers get a set of products together at a special price. You decide the bundle price (e.g., ₹999), and the discount applies only when all the items in the bundle are present in the cart. Once the shopper adds the full bundle, the final price automatically adjusts to the bundle price.',
      examples: [
        'Buy Shampoo + Conditioner + Hair Serum together for ₹999.',
        'Get a T-shirt + Shorts combo for ₹799 when both items are in the cart.',
        'Buy Face Wash + Toner + Moisturizer as a skincare set for ₹1,199.'
      ],
      importantNote: 'All items in the bundle must be added to the cart for the discount to apply.'
    },
    permissionId: permissionType.BUNDLE_DISCOUNTS,
    action: {
      type: 'LINK',
      link: '/discounts/v2/create-discount?type=BUNDLE'
    }
  },
  BXGY: {
    label: 'Buy X Get Y',
    value: 'BUY_X_GET_Y',
    sidePanel: {
      title: 'Buy X Get Y',
      description:
        'This discount applies only when both products, the qualifying product (X) and the reward product (Y) are present in the cart. Once both items are added, the system applies the discount on Y. This format encourages cross-sell and ensures the shopper explicitly chooses the reward product.',
      examples: [
        'Buy 2 Soaps and get a Facewash at 50% off.',
        'Buy a Hoodie and get a Cap for ₹199.',
        'Buy any Shoes and get Socks at 30% off.',
        'Buy a Coffee Maker and get a Mug free.'
      ],
      importantNote:
        'Product Y must be manually added by the shopper. The discount applies only when both X and Y are in the cart.'
    },
    action: {
      type: 'LINK',
      link: '/discounts/v2/create-discount?type=BXGY'
    }
  },
  BXGY_AUTO_ADDED: {
    label: 'Buy X Get Y (Auto added)',
    value: 'BUY_X_GET_Y_AUTO_ADDED',
    sidePanel: {
      title: 'Buy X Get Y auto added',
      description:
        'When the shopper adds product X, product Y is automatically added to the cart, once the discount is applied, either free or at a discounted price. The customer doesn’t need to add Y themselves. The offer applies instantly, making the reward easy to claim and hard to miss.',
      examples: [
        'Buy any Perfume and get a Mini Perfume for free.',
        'Buy 2 Shampoos and get a Conditioner at 50% off.',
        'Buy any T-shirt and get a Tote Bag for ₹99.',
        'Buy a Protein Jar and get a Shaker Bottle as a free gift.'
      ],
      importantNote:
        'Product Y does NOT need to be in the cart. It will be auto-added as soon as the customer adds product X and applies the discount.'
    },
    action: {
      type: 'LINK',
      link: '/discounts/v2/create-discount?type=BXGY_AUTO_ADDED'
    }
  },
  FREEBIE: {
    label: 'Freebie',
    value: 'FREEBIE',
    sidePanel: {
      title: 'Freebie',
      description:
        'A free product is automatically added to the cart once the customer meets a condition, usually cart value. Helps make high-value orders feel more rewarding.',
      examples: [
        'Get a free travel pouch on orders above ₹1499.',
        'Get a free pair of Socks when you buy any two T-shirts.',
        'Receive a free Sample Kit on any Skincare order above ₹1,000.'
      ],
      importantNote:
        'The free product will be auto-added when the offer applies, so the shopper doesn’t need to have the product in the cart.'
    },
    action: {
      type: 'LINK',
      link: '/discounts/v2/create-discount?type=FREEBIE'
    }
  },
  FREEBIE_SELECTOR: {
    label: 'Discounted product selector',
    value: 'FREEBIE_SELECTOR',
    sidePanel: {
      title: 'Discounted product selector',
      description:
        'Lets the customer choose their preferred free gift from a curated list once they qualify. This increases perceived value and gives customers a sense of control, improving conversion.',
      examples: [
        'On orders above ₹2000, choose your free gift: Tote Bag / Socks / Keychain',
        'Spend ₹2,499 and pick one freebie: Mini Perfume / Travel Pouch / Hand Cream.',
        'On skincare orders over ₹1,200, choose any 2 minis: Vitamin C Serum / Moisturizer / Cleanser / Toner.'
      ],
      importantNote: 'A popup selection will appear for the user in the checkout.'
    },
    permissionId: permissionType.DISCOUNTED_PRODUCT_SELECTOR,
    action: {
      type: 'LINK',
      link: '/discounts/v2/create-discount?type=FREEBIE_SELECTOR'
    }
  },
  CALLOUT_CARD: {
    label: 'Callout card',
    value: 'CALLOUT_CARD',
    permissionId: permissionType.CREATE_CALLOUT_CARDS,
    sidePanel: {
      title: 'Callout card',
      description: 'Create informational cards or banners to highlight offers, bank discounts, or important messages.',
      examples: ['10% Instant Discount on HDFC Cards', 'COD available on orders below ₹5000'],
      importantNote: 'These do not apply a discount logic but serve as visual indicators.'
    },
    action: {
      type: 'LINK',
      link: '/discounts/create-callout-card'
    }
  },
  DISCOUNTS_SETS: {
    label: 'Discount Sets',
    value: 'DISCOUNTS_SETS',
    permissionId: permissionType.BULK_CREATE_DISCOUNTS,
    sidePanel: {
      title: 'Discount Sets',
      description: 'Generate a large set of unique coupon codes for a specific campaign or affiliate marketing.',
      examples: ['Generate 1000 unique codes for influencers'],
      importantNote: 'Useful for tracking performance of individual partners.'
    },
    action: {
      type: 'LINK',
      link: '/discounts/create-discount-set'
    }
  },
  EXIT_COUPON: {
    label: 'Exit Coupon',
    value: 'EXIT_COUPON',
    permissionId: permissionType.CREATE_EXIT_COUPONS,
    sidePanel: {
      title: 'Exit Coupon',
      description: 'Show a targeted discount when a customer tries to exit checkout, helping recover potentially lost sales.',
      examples: ['Get 25% off if you complete your order now'],
      importantNote: 'Triggered only when a customer attempts to exit the checkout flow.'
    },
    action: {
      type: 'LINK',
      link: '/discounts/create-exit-coupon'
    }
  }
}

export const initialPreview = {
  header: '',
  description: '',
  longDescription: ''
}
